import { useMemo, useState } from "react";
import { useToggleVerificationExportInclusion } from "../../../../common/api/queries";
import { Permission } from "../../../../common/security/enums";
import { useCurrentAgentAccount, useHasPermissions } from "../../../auth/hooks";
import { ContractVerificationStatus } from "../../enums";
import { Contract } from "../../types";
import { canSendContractToBackoffice, contractPermissionsByContractType } from "../../utils";
import ContractChangeVerificationStatusForm from "../forms/ContractChangeVerificationStatusForm";
import { ContractOriginTag } from "./ContractOriginTag";
import ContractStatusTag from "./ContractStatusTag";
import ContractVerificationStatusTag from "./ContractVerificationStatusTag";

interface Props {
  contract: Contract;
}

export const ContractTags = ({ contract }: Props) => {
  const [verificationStatusFormOpen, setVerificationStatusFormOpen] = useState(false);

  const userAccount = useCurrentAgentAccount();
  const hasVerificationExportPermission = useHasPermissions(Permission.CONTRACT_VERIFICATION_EXPORT);
  const hasVerificationStatusChangePermission = useHasPermissions(
    contractPermissionsByContractType[contract.type].verificationStatusChange,
  );

  const toggleVerificationExportInclusion = useToggleVerificationExportInclusion();

  const canSendToBackoffice = useMemo(
    () => Boolean(userAccount && canSendContractToBackoffice(userAccount, contract)),
    [userAccount, contract],
  );

  const isSentForVerification = contract.verificationStatus === ContractVerificationStatus.SENT;
  const canChangeVerificationNote =
    hasVerificationStatusChangePermission || (canSendToBackoffice && isSentForVerification);
  const canToggleVerificationExport = isSentForVerification && hasVerificationExportPermission;

  return (
    <>
      <ContractStatusTag status={contract.status} />

      {contract.verificationStatus && (
        <ContractVerificationStatusTag
          status={contract.verificationStatus}
          note={contract.verificationNote}
          includedInExport={contract.includeInVerificationExport}
          onNoteChangeClick={canChangeVerificationNote ? () => setVerificationStatusFormOpen(true) : undefined}
          onExportToggleClick={
            canToggleVerificationExport
              ? () =>
                  toggleVerificationExportInclusion.mutate({
                    id: contract.id,
                    object: { optimisticLockVersion: contract.optimisticLockVersion as number },
                  })
              : undefined
          }
        />
      )}

      {contract.origin && <ContractOriginTag origin={contract.origin} isWide />}

      <ContractChangeVerificationStatusForm
        open={verificationStatusFormOpen}
        contract={contract}
        viewType="noteEdit"
        onFormCancel={() => setVerificationStatusFormOpen(false)}
      />
    </>
  );
};
